import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Trash2, Database, FileText, Sparkles, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';

interface KnowledgeDoc {
  id: string;
  source_type: 'post' | 'faq' | 'manual' | 'page';
  title: string;
  url: string | null;
  locale: string;
  content: string;
  enabled: boolean;
  chunks_count?: number;
  indexed_at: string | null;
  updated_at: string;
}

interface Paginated<T> { data: T[]; total: number; current_page: number; last_page: number; }

interface Stats {
  documents: number;
  documents_enabled: number;
  chunks: number;
  chunks_embedded: number;
  embeddings_available: boolean;
  embeddings_provider: string;
  embeddings_model: string;
  vector_driver: string;
  vector_store: {
    driver: string;
    collection?: string;
    healthy: boolean;
    points?: number | null;
  };
}

const SOURCE_LABEL: Record<KnowledgeDoc['source_type'], string> = {
  post: 'Post', faq: 'FAQ', manual: 'Manual', page: 'Página',
};

export function KnowledgeBase() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<KnowledgeDoc | 'new' | null>(null);

  const { data: stats } = useQuery({ queryKey: ['kb-stats'], queryFn: () => unwrap<Stats>(api.get('/ai/knowledge/stats')) });
  const { data, isLoading } = useQuery({
    queryKey: ['kb-docs'],
    queryFn: () => unwrap<Paginated<KnowledgeDoc>>(api.get('/ai/knowledge?per_page=50')),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['kb-docs'] });
    void qc.invalidateQueries({ queryKey: ['kb-stats'] });
  }

  const syncPosts = useMutation({
    mutationFn: () => api.post('/ai/knowledge/sync-posts', {}),
    onSuccess: (res) => { toast.success(res.data?.message ?? 'Posts sincronizados'); refresh(); },
    onError: () => toast.error('No se pudieron sincronizar los posts'),
  });

  const reindex = useMutation({
    mutationFn: (id: string) => api.post(`/ai/knowledge/${id}/reindex`, {}),
    onSuccess: () => { toast.success('Documento reindexado'); refresh(); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/ai/knowledge/${id}`),
    onSuccess: () => { toast.success('Documento eliminado'); refresh(); },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={<FileText className="size-4" />} label="Documentos" value={`${stats?.documents_enabled ?? 0}/${stats?.documents ?? 0}`} hint="activos / total" />
        <StatCard icon={<Database className="size-4" />} label="Fragmentos" value={String(stats?.chunks ?? 0)} hint="trozos indexados" />
        <StatCard icon={<Sparkles className="size-4" />} label="Con embedding" value={String(stats?.chunks_embedded ?? 0)} hint="vectorizados" />
        <StatCard
          icon={<Sparkles className="size-4" />}
          label="Embeddings"
          value={stats?.embeddings_available ? 'Activos' : 'No config.'}
          hint={stats?.embeddings_available ? `${stats.embeddings_provider}/${stats.embeddings_model}` : 'búsqueda por palabras'}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Base de conocimiento</CardTitle>
              <CardDescription>Lo que el chatbot usa para responder: posts del blog, FAQs y notas manuales.</CardDescription>
              {stats && <VectorStoreBadge stats={stats} />}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => syncPosts.mutate()} disabled={syncPosts.isPending}>
                <RefreshCw className={`size-4 ${syncPosts.isPending ? 'animate-spin' : ''}`} /> Sincronizar posts
              </Button>
              <Button onClick={() => setEditing('new')}>
                <Plus className="size-4" /> Añadir documento
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Título</th>
                <th className="px-4 py-2">Origen</th>
                <th className="px-4 py-2">Fragmentos</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>}
              {data?.data.length === 0 && !isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Sin documentos. Sincroniza los posts o añade uno manual.</td></tr>
              )}
              {data?.data.map((doc) => (
                <tr key={doc.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <button className="text-left font-medium hover:underline" onClick={() => setEditing(doc)}>{doc.title}</button>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{SOURCE_LABEL[doc.source_type]}</Badge></td>
                  <td className="px-4 py-3 text-neutral-600">{doc.chunks_count ?? 0}</td>
                  <td className="px-4 py-3">{doc.enabled ? <Badge variant="success">Activo</Badge> : <Badge variant="secondary">Inactivo</Badge>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" title="Reindexar" onClick={() => reindex.mutate(doc.id)} disabled={reindex.isPending}>
                        <RotateCw className="size-4" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Eliminar" onClick={() => { if (confirm('¿Eliminar este documento?')) remove.mutate(doc.id); }}>
                        <Trash2 className="size-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <DocDialog doc={editing} onClose={() => setEditing(null)} onSaved={refresh} />
    </div>
  );
}

function VectorStoreBadge({ stats }: { stats: Stats }) {
  const isQdrant = stats.vector_store.driver === 'qdrant';
  if (!isQdrant) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
        <span className="inline-block size-2 rounded-full bg-neutral-300" />
        Índice vectorial: local (coseno en Postgres)
      </div>
    );
  }
  const healthy = stats.vector_store.healthy;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
      <span className={`inline-block size-2 rounded-full ${healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
      Qdrant
      <Badge variant="outline">{stats.vector_store.collection}</Badge>
      <span>{healthy ? `${stats.vector_store.points ?? 0} vectores` : 'no accesible · usando fallback local'}</span>
    </div>
  );
}

function StatCard({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-neutral-400">{icon}{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      <div className="text-xs text-neutral-400">{hint}</div>
    </div>
  );
}

function DocDialog({ doc, onClose, onSaved }: { doc: KnowledgeDoc | 'new' | null; onClose: () => void; onSaved: () => void }) {
  const isNew = doc === 'new';
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (doc && doc !== 'new') {
      setTitle(doc.title); setContent(doc.content); setUrl(doc.url ?? ''); setEnabled(doc.enabled);
    } else if (doc === 'new') {
      setTitle(''); setContent(''); setUrl(''); setEnabled(true);
    }
  }, [doc]);

  const save = useMutation({
    mutationFn: () => {
      const payload = { title, content, url: url || null, enabled, source_type: 'manual' as const };
      return isNew
        ? api.post('/ai/knowledge', payload)
        : api.patch(`/ai/knowledge/${(doc as KnowledgeDoc).id}`, payload);
    },
    onSuccess: () => { toast.success(isNew ? 'Documento creado' : 'Documento actualizado'); onSaved(); onClose(); },
    onError: () => toast.error('No se pudo guardar'),
  });

  const readOnlyPost = doc && doc !== 'new' && doc.source_type === 'post';

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Nuevo documento' : 'Editar documento'}</DialogTitle>
          <DialogDescription>
            {readOnlyPost ? 'Este documento proviene de un post; edítalo desde el contenido.' : 'El contenido se trocea y vectoriza automáticamente al guardar.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="doc-title">Título</Label>
            <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!!readOnlyPost} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="doc-url">URL (opcional)</Label>
            <Input id="doc-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" disabled={!!readOnlyPost} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="doc-content">Contenido</Label>
            <Textarea id="doc-content" rows={10} value={content} onChange={(e) => setContent(e.target.value)} disabled={!!readOnlyPost} placeholder="Texto, FAQ, descripción de servicios…" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Activo (el chatbot lo usa)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {!readOnlyPost && (
            <Button onClick={() => save.mutate()} disabled={save.isPending || !title || !content}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
